import * as React from 'react'

import { ReportShares } from 'modules/reports/store/collections/reports_shares'
import type Report from 'modules/reports/store/models/Report'
import {
  type ReportShare,
  type ReportShareMode,
  ReportShareModes,
  type ReportShareVisibility,
} from 'modules/reports/store/models/ReportShare'

import { ReportShareForm } from './ReportShareForm'

export interface ReportSharesEditor {
  /**
   * The current state of the shares being edited.
   */
  currentShares: ReportShareInfo[]
  /**
   * Adds new shares
   */
  addShares: (shares: Array<{ granteeId: number; visibility: ReportShareVisibility }>) => void

  changeVisibility: (granteeId: number, visibility: ReportShareVisibility) => void
  /**
   * Removes an existing share (persisted or not).
   */
  removeShare: (granteeId: number) => void
  /**
   * Persist all changes to the shares.
   */
  saveChanges: () => Promise<void>
  /**
   * Resets the internal state of the hook to a given set of shares, usually on the initial fetch or after a succesful save.
   */
  reset: (shares: ReportShare[]) => void
}

export interface ReportShareInfo {
  id?: number
  granteeId: number
  mode: ReportShareMode
  visibility: ReportShareVisibility
}

type GranteeId = number

/**
 * Manages an in-memory representation of set of ReportShares that can be edited and then persisted all at once.
 */
export function useReportSharesEditor(report: Report, granterId: number): ReportSharesEditor {
  const [shareForms, setShareForms] = React.useState<Map<GranteeId, ReportShareForm>>(new Map())

  const currentShares: ReportShareInfo[] = [...shareForms.values()]
    .filter((form) => !form.isDeleted)
    .map((form) => ({
      id: form.id,
      granteeId: form.granteeId,
      mode: form.mode,
      visibility: form.visibility,
    }))

  const addShares = (shares: Array<{ granteeId: number; visibility: ReportShareVisibility }>) => {
    const updatedForms = new Map(shareForms)

    for (const { granteeId, visibility } of shares) {
      let share = shareForms.get(granteeId)

      if (share) {
        if (share.isDeleted) {
          share.restore()
        } else {
          throw new Error("Invariant: Can't add two shares for the same granteeId")
        }
      } else {
        share = new ReportShareForm({
          granterId,
          granteeId: granteeId,
          visibility: visibility,
          mode: ReportShareModes.Personal,
          status: 'new',
        })
      }

      updatedForms.set(granteeId, share)
    }

    setShareForms(updatedForms)
  }

  const changeVisibility = (granteeId: GranteeId, visibility: ReportShareVisibility) => {
    const share = shareForms.get(granteeId)

    if (!share) {
      return
    }

    share.changeVisibility(visibility)

    const updatedForms = new Map(shareForms)
    updatedForms.set(granteeId, share)
    setShareForms(updatedForms)
  }

  const removeShare = (granteeId: GranteeId) => {
    const share = shareForms.get(granteeId)

    if (!share) {
      return
    }

    const updatedForms = new Map(shareForms)

    if (share.isPersisted) {
      share.delete()
      updatedForms.set(granteeId, share)
    } else {
      updatedForms.delete(granteeId)
    }

    setShareForms(updatedForms)
  }

  const saveChanges = async () => {
    const forms = [...shareForms.values()]

    const deletions = forms
      .filter((form) => form.isDeleted)
      .map((form) => {
        const share = ReportShares.get(form.id)

        if (!share) {
          throw Error(`Share ${form.id} does not exist`)
        }

        return share.delete()
      })

    const creations = forms
      .filter((form) => form.isNew)
      .map((form) => {
        return report.share(form.granteeId, form.mode, form.visibility)
      })

    const modifications = forms
      .filter((form) => form.isModified)
      .map((form) => {
        const share = ReportShares.get(form.id)

        if (!share) {
          throw Error(`Share ${form.id} does not exist`)
        }

        const modify = async () => {
          await share.update({ metadata: { mode: form.mode, visibility: form.visibility } })
        }

        return modify()
      })

    await Promise.all([...creations, ...modifications, ...deletions])
  }

  const reset = (shares: ReportShare[]) => {
    const forms = shares.map((share) => ReportShareForm.fromExistingShare(share))
    setShareForms(new Map(forms.map((form) => [form.granteeId, form])))
  }

  return {
    // TODO: Minimize re-renders with useCallback()
    currentShares: currentShares,
    addShares: addShares,
    changeVisibility: changeVisibility,
    removeShare: removeShare,
    saveChanges: saveChanges,
    reset: reset,
  }
}
